import { useMemo, useRef } from 'react'

import type { IconType } from 'react-icons'
import { HiMiniChevronDown } from 'react-icons/hi2'
import { TbPilcrow } from 'react-icons/tb'

import Button from '@/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  getDropdownMenuItemClassName,
} from '@/components/common/DropdownMenu'
import Popover from '@/components/common/PopoverV2'
import { Surface } from '@/components/common/TextArea/RichTextArea/menus/components/Surface'
import { Toolbar } from '@/components/common/TextArea/RichTextArea/menus/components/Toolbar'

export type ContentTypeDropdownOption = {
  label: string
  id: string
  type: 'option'
  disabled: () => boolean
  isActive: () => boolean
  onClick: () => void
  icon: IconType
}

export type ContentTypeDropdownCategory = {
  label: string
  id: string
  type: 'category'
}

export type ContentPickerOptions = Array<
  ContentTypeDropdownOption | ContentTypeDropdownCategory
>

export type ContentTypeDropdownProps = {
  options: ContentPickerOptions
  disabled?: boolean
  expandedTrigger?: boolean
  initialLabel?: string
  triggerClassName?: string
}

const isOption = (
  option: ContentTypeDropdownOption | ContentTypeDropdownCategory,
): option is ContentTypeDropdownOption => {
  return option.type === 'option'
}

const isCategory = (
  option: ContentTypeDropdownOption | ContentTypeDropdownCategory,
): option is ContentTypeDropdownCategory => {
  return option.type === 'category'
}

export const ContentTypePopover: React.FC<ContentTypeDropdownProps> = ({
  options,
  disabled,
  expandedTrigger,
  initialLabel,
  triggerClassName,
}) => {
  const activeItem = useMemo(
    () =>
      options.find((option) => option.type === 'option' && option.isActive()),
    [options],
  )
  const hasResolvedInitialActiveItemRef = useRef(false)

  if (activeItem) {
    hasResolvedInitialActiveItemRef.current = true
  }

  const triggerLabel =
    activeItem?.label ??
    (!hasResolvedInitialActiveItemRef.current ? initialLabel : undefined) ??
    'Paragraph'

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
  }

  return (
    <Popover modal>
      <Popover.Trigger asChild={true} disabled={disabled}>
        {expandedTrigger ? (
          <Button
            variant="outline-compact"
            className="flex items-center justify-between active:scale-100 min-w-[132px]"
            disabled={disabled}
          >
            <div className="flex items-center gap-x-[6px]">
              <span className="text-sm font-medium select-none">
                {triggerLabel}
              </span>
            </div>
            <HiMiniChevronDown className="flex-shrink-0 w-[15px] h-[15px]" />
          </Button>
        ) : (
          <Toolbar.Button
            isActive={activeItem?.id !== 'paragraph' && !!activeItem?.type}
            onClick={handleClick}
            disabled={disabled}
            className={triggerClassName}
          >
            <TbPilcrow className="w-4 h-4" />
          </Toolbar.Button>
        )}
      </Popover.Trigger>
      <Popover.Content
        // The icon trigger sits inset inside a bordered group, so it takes that
        // inset back to clear the border by the same gap as every other menu.
        sideOffset={expandedTrigger ? undefined : 6}
        className="p-0 w-fit"
      >
        <Surface className="space-y-[2px] p-[6px] shadow-none border-none">
          {options.map((option) => {
            if (isOption(option)) {
              return (
                // make this look like a dropdown menu item;
                // can't use DropdownMenuItem because it doesn't seem to work well within
                // tiptap bubble menus (e.g. click on an item or the trigger causes the text underneath
                // the menu to be selected)
                <div
                  key={option.id}
                  onClick={option.onClick}
                  className={getDropdownMenuItemClassName({
                    isActive: option.isActive(),
                    className: 'hover:bg-primary-100 dark:hover:bg-primary-800',
                  })}
                >
                  <option.icon className="w-4 h-4 mr-1" />
                  <span className="text-sm">{option.label}</span>
                </div>
              )
            } else if (isCategory(option)) {
              return (
                <div className="pt-2 first:pt-0 pl-2" key={option.id}>
                  <span className="text-sm font-semibold text-primary-900 dark:text-primary-100">
                    {option.label}
                  </span>
                </div>
              )
            }
          })}
        </Surface>
      </Popover.Content>
    </Popover>
  )
}

export const ContentTypeDropdown: React.FC<ContentTypeDropdownProps> = ({
  options,
  disabled = false,
  initialLabel,
}) => {
  const activeItem = useMemo(
    () =>
      options.find((option) => option.type === 'option' && option.isActive()),
    [options],
  )
  const hasResolvedInitialActiveItemRef = useRef(false)

  if (activeItem) {
    hasResolvedInitialActiveItemRef.current = true
  }

  const triggerLabel =
    activeItem?.label ??
    (!hasResolvedInitialActiveItemRef.current ? initialLabel : undefined) ??
    'Paragraph'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true} disabled={disabled}>
        <Button
          variant="outline-compact"
          className="flex items-center justify-between active:scale-100 min-w-[132px]"
          disabled={disabled}
        >
          <div className="flex items-center gap-x-[6px]">
            <span className="text-sm font-medium select-none">
              {triggerLabel}
            </span>
          </div>
          <HiMiniChevronDown className="flex-shrink-0 w-[15px] h-[15px]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-fit">
        {options.map((option) => {
          if (isOption(option)) {
            return (
              <DropdownMenuItem key={option.id} onClick={option.onClick}>
                <option.icon className="w-4 h-4 mr-1" />
                {option.label}
              </DropdownMenuItem>
            )
          }
          if (isCategory(option)) {
            return (
              <DropdownMenuLabel key={option.id} className="mt-2 first:mt-0">
                {option.label}
              </DropdownMenuLabel>
            )
          }
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
